import * as React from "react";

import { Check, ChevronsUpDown, Loader2, X } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "ui/components/command";
import { LOADER2, USER } from "icons";
import { Popover, PopoverContent, PopoverTrigger } from "ui/components/popover";

import { Button } from "ui/components/button";
import Icon from "./icon";
import { Label } from "ui/components/label";
import { cn } from "ui/lib/utils";

/**
 * Combobox (tech-agnostic)
 * - UI-only, controlled props
 * - Consumers provide data and fetching outside
 * - Optional fetchSelected to resolve a value not present in data
 */

export default function Combobox({
  // Data
  data = [], // [{ value, label, select? }]
  value,
  onChange,
  // Search
  searchable = true,
  search = "",
  onSearch,
  // UI
  label,
  required = false,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  emptyMessage = "No items found.",
  isLoading = false,
  disabled = false,
  className,
  // Behavior
  returnItem = false,
  shouldFilter = false, // rely on parent by default
  // Optional resolver for selected value when not present in data
  fetchSelected, // async (value) => mappedItem
  // Clear controls
  clearable = true,
  clearAriaLabel = "Clear selection",
}) {
  const [open, setOpen] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState(null);

  // Keep selected item in sync
  React.useEffect(() => {
    if (!value) {
      setSelectedItem(null);
      return;
    }
    const found = data.find((item) =>
      returnItem ? item?.value === value.value : item?.value === value,
    );
    if (found) {
      setSelectedItem(found);
      return;
    }
    let ignore = false;
    const resolve = async () => {
      if (!fetchSelected) return;
      try {
        const fetched = await fetchSelected(value);
        if (!ignore && fetched) setSelectedItem(fetched);
      } catch (_) {
        if (!ignore) setSelectedItem(null);
      }
    };
    resolve();
    return () => {
      ignore = true;
    };
  }, [value, data, fetchSelected]);

  const filteredData = React.useMemo(() => {
    if (!shouldFilter || !search) return data;
    const s = String(search).toLowerCase();
    return data.filter((d) =>
      String(d.label ?? d.select ?? d.value)
        .toLowerCase()
        .includes(s),
    );
  }, [data, shouldFilter, search]);

  const handleSelect = (currentValue) => {
    const selected = filteredData.find(
      (item) => String(item.value) === String(currentValue),
    );
    const newSelected = selectedItem?.value === currentValue ? null : selected;
    if (!newSelected) onSearch?.("");
    setSelectedItem(newSelected);
    if (returnItem) onChange?.(newSelected || null);
    else onChange?.(newSelected?.value || "");
    setOpen(false);
  };

  const handleClearSelection = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    onSearch?.("");
    setSelectedItem(undefined);
    if (returnItem) onChange?.(undefined);
    else onChange?.(undefined);
  };

  if (label) {
    return (
      <div className={cn("space-y-2", className)}>
        <Label
          className={cn(
            "text-sm",
            required && "after:content-['*'] after:ml-0.5 after:text-red-500",
          )}
        >
          {label}
        </Label>

        <Popover
          open={open}
          onOpenChange={setOpen}
          disabled={disabled || isLoading}
          modal
        >
          <PopoverTrigger asChild disabled={disabled || isLoading}>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              aria-busy={isLoading}
              disabled={disabled || isLoading}
              className={cn(
                "w-full justify-between",
                !selectedItem && "text-muted-foreground",
              )}
            >
              <div className="flex items-center flex-1 min-w-0">
                <div className={cn(!selectedItem && "text-muted-foreground", "truncate")}>
                  {selectedItem
                    ? (selectedItem.select ?? selectedItem.label)
                    : placeholder}
                </div>
              </div>
              <div className="flex items-center gap-1">
                {clearable && !!selectedItem && !isLoading && (
                  <span
                    role="button"
                    aria-label={clearAriaLabel}
                    className="opacity-70 hover:opacity-100 cursor-pointer"
                    onClick={handleClearSelection}
                  >
                    <X className="h-4 w-4" />
                  </span>
                )}
                {isLoading ? (
                  <Loader2 className="opacity-70 animate-spin" />
                ) : (
                  <ChevronsUpDown className="opacity-50" />
                )}
              </div>
            </Button>
          </PopoverTrigger>

          <PopoverContent
            className={cn("p-0 !w-[var(--radix-popover-trigger-width)]")}
          >
            <Command shouldFilter={false}>
              {searchable && (
                <div className="relative px-0.5 py-0.5">
                  <CommandInput
                    placeholder={searchPlaceholder}
                    className="h-9 pr-8"
                    value={search}
                    onValueChange={onSearch}
                  />
                  {search && (
                    <button
                      type="button"
                      aria-label="Clear search"
                      className="absolute right-2 top-2.5 text-muted-foreground hover:text-foreground"
                      onClick={() => onSearch?.("")}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              )}
              <CommandList>
                {!isLoading && (
                  <>
                    <CommandGroup>
                      {filteredData.map((item) => (
                        <CommandItem
                          key={item.value}
                          value={String(item.value)}
                          onSelect={handleSelect}
                        >
                          {item.label}
                          <Check
                            className={cn(
                              "ml-auto",
                              selectedItem?.value === item.value
                                ? "opacity-100"
                                : "opacity-0",
                            )}
                          />
                        </CommandItem>
                      ))}
                    </CommandGroup>
                    <CommandEmpty>{emptyMessage}</CommandEmpty>
                  </>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    );
  }

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      disabled={disabled || isLoading}
      modal
    >
      <PopoverTrigger asChild disabled={disabled || isLoading}>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-busy={isLoading}
          disabled={disabled || isLoading}
          className={cn(
            "w-full justify-between",
            !selectedItem && "text-muted-foreground",
            className,
          )}
        >
          <div className="flex items-center flex-1 min-w-0">
            <div
              className={cn(
                !selectedItem && "text-muted-foreground",
                "truncate",
              )}
            >
              {selectedItem
                ? (selectedItem.select ?? selectedItem.label)
                : placeholder}
            </div>
          </div>
          <div className="flex items-center gap-1">
            {clearable && !!selectedItem && !isLoading && (
              <span
                role="button"
                aria-label={clearAriaLabel}
                className="opacity-70 hover:opacity-100 cursor-pointer"
                onClick={handleClearSelection}
              >
                <X className="h-4 w-4" />
              </span>
            )}
            {isLoading ? (
              <Loader2 className="opacity-70 animate-spin" />
            ) : (
              <ChevronsUpDown className="opacity-50" />
            )}
          </div>
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className={cn("p-0 !w-[var(--radix-popover-trigger-width)]")}
      >
        <Command shouldFilter={false}>
          {searchable && (
            <div className="relative px-0.5 py-0.5">
              <CommandInput
                placeholder={searchPlaceholder}
                className="h-9 pr-8"
                value={search}
                onValueChange={onSearch}
              />
              {search && (
                <button
                  type="button"
                  aria-label="Clear search"
                  className="absolute right-2 top-2.5 text-muted-foreground hover:text-foreground"
                  onClick={() => onSearch?.("")}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
          <CommandList>
            {!isLoading && (
              <>
                <CommandGroup>
                  {filteredData.map((item) => (
                    <CommandItem
                      key={item.value}
                      value={String(item.value)}
                      onSelect={handleSelect}
                    >
                      {item.label}
                      <Check
                        className={cn(
                          "ml-auto",
                          selectedItem?.value === item.value
                            ? "opacity-100"
                            : "opacity-0",
                        )}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
                <CommandEmpty>{emptyMessage}</CommandEmpty>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function ComboboxDemo({
  data = [],
  selectedItem: propSelectedItem,
  onChange,
  search = "",
  onSearch,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  disabled = false,
  className,
  isLoading = false,
  icon,
}) {
  const [open, setOpen] = React.useState(false);

  const [selectedItem, setSelectedItem] = React.useState(
    propSelectedItem || null,
  );
  // Update selectedItem when value prop changes
  React.useEffect(() => {
    setSelectedItem(propSelectedItem || null);
  }, [propSelectedItem]);

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      disabled={disabled}
      modal={true}
    >
      <PopoverTrigger asChild disabled={disabled}>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between",
            !selectedItem && "text-muted-foreground",
            className,
          )}
        >
          <div className="flex items-center">
            {icon && (
              <Icon
                icon={icon}
                className={cn("mr-2", !selectedItem && "text-muted-foreground")}
              />
            )}
            <div
              className={cn(
                " text-left truncate max-w-40",
                !selectedItem && "text-muted-foreground",
              )}
            >
              {selectedItem ? selectedItem.select : placeholder}
            </div>
          </div>
          <ChevronsUpDown className="opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className={cn("p-0 !w-[var(--radix-popover-trigger-width)]")}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            className="h-9"
            onValueChange={(value) => onSearch(value)}
          />
          <CommandList>
            {isLoading ? (
              <div className="flex justify-center items-center p-4 text-muted-foreground">
                {/* <Icon icon={LOADER2} className="animate-spin h-4 w-4 mr-2" /> */}
                Loading...
              </div>
            ) : (
              <>
                <CommandGroup>
                  {data.map((item) => (
                    <CommandItem
                      key={item.value}
                      value={item.value}
                      onSelect={(currentValue) => {
                        const selectedItemObject = data.find(
                          (item) => item.value === currentValue,
                        );

                        const newSelectedItem =
                          selectedItem?.value === currentValue
                            ? null
                            : selectedItemObject;

                        if (newSelectedItem === null) {
                          onSearch("");
                        }

                        setSelectedItem(newSelectedItem);
                        onChange?.(newSelectedItem);
                        setOpen(false);
                      }}
                    >
                      {item.label}
                      <Check
                        className={cn(
                          "ml-auto",
                          selectedItem?.value === item.value
                            ? "opacity-100"
                            : "opacity-0",
                        )}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
                <CommandEmpty>No items found.</CommandEmpty>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
